import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import {
  GuidePageShell,
  GuideSection,
  GuideSubtitle,
  GuideFormula,
  GuideExample,
  GuideCallout,
  GuideTable,
  GuideMetricRow,
  GuideFaq,
} from '../components/guide/GuideLayout';
import { BRAND } from '../theme/brandTheme';
import '../styles/guide-page.css';

const METRICS_GUIDE_TOC = [
  { id: 'overview', label: 'Metrikler' },
  { id: 'conversion', label: 'Conv. Rate' },
  { id: 'uplift', label: 'Uplift' },
  { id: 'bayesian', label: 'Significance' },
  { id: 'revenue', label: 'Gelir Tahmini' },
  { id: 'summary', label: 'Özet' },
  { id: 'faq', label: 'SSS' },
];

/** Metrik rehberi — raporlarda gösterilen değerlerin hesabı */
const ReportCalculationGuide = () => (
  <GuidePageShell
    badge="Rapor metrikleri · hype CRO"
    title="Metrik Rehberi"
    subtitle="A/B test raporlarında gösterilen metriklerin nasıl hesaplandığı — sade ve anlaşılır açıklamalar."
    toc={METRICS_GUIDE_TOC}
    footerActions={[
      { label: 'AB Test Hesaplayıcı', to: '/calculator', icon: <CalculateIcon /> },
      { label: 'Yöntemler Rehberi', to: '/methods-guide', variant: 'outlined' },
    ]}
  >
    <GuideSection
      id="overview"
      number="01"
      title="Raporda Gösterilen Metrikler"
      lead="Dashboard ve rapor detayında gördüğünüz her sütunun kısa tanımı."
    >
      <GuideMetricRow pill="Session" pillColor={BRAND.purple}>
        <strong>Ziyaretçi sayısı</strong> — Her grupta kaç kişi test edildi
      </GuideMetricRow>
      <GuideMetricRow pill="Conv." pillColor={BRAND.green}>
        <strong>Dönüşüm sayısı</strong> — Hedef eylemi gerçekleştiren kullanıcı (satın alma, kayıt vb.)
      </GuideMetricRow>
      <GuideMetricRow pill="CR" pillColor="#6588DF">
        <strong>Dönüşüm oranı</strong> — Ziyaretçilerin yüzde kaçı dönüştü
      </GuideMetricRow>
      <GuideMetricRow pill="Uplift" pillColor="#E57702">
        <strong>İyileşme oranı</strong> — Varyasyonun kontrole göre performans farkı
      </GuideMetricRow>
      <GuideMetricRow pill="Signif." pillColor={BRAND.redLink}>
        <strong>İstatistiksel anlamlılık</strong> — Sonucun güvenilir olup olmadığı
      </GuideMetricRow>
      <GuideMetricRow pill="Gelir" pillColor="#9B59B6">
        <strong>Aylık / yıllık tahmin</strong> — Kazanan varyasyon tüm trafiğe uygulanırsa ek kazanç
      </GuideMetricRow>
    </GuideSection>

    <GuideSection
      id="conversion"
      number="02"
      title="Conversion Rate (Dönüşüm Oranı)"
      lead="Ziyaretçilerinizin ne kadarının hedef eylemi gerçekleştirdiğini gösterir."
    >
      <GuideFormula formula="Dönüşüm Oranı = (Dönüşüm Sayısı / Ziyaretçi Sayısı) × 100" />
      <GuideExample title="Kontrol">
        10.000 ziyaretçi · 200 satın alma → <strong>2.00%</strong>
      </GuideExample>
      <GuideExample title="Varyasyon 1">
        10.000 ziyaretçi · 250 satın alma → <strong>2.50%</strong>
      </GuideExample>
      <p style={{ fontFamily: BRAND.fontFamily, fontSize: 14, color: BRAND.textMuted, fontStyle: 'italic' }}>
        Varyasyon 1&apos;de her 100 ziyaretçiden 2.5&apos;i satın alma yapıyor; kontrolde 2&apos;si.
      </p>
    </GuideSection>

    <GuideSection
      id="uplift"
      number="03"
      title="Uplift (İyileşme Oranı)"
      lead="Varyasyonun kontrol grubuna göre yüzde kaç daha iyi veya kötü olduğunu ifade eder."
    >
      <GuideFormula formula="Uplift = ((Varyasyon CR − Kontrol CR) / Kontrol CR) × 100" />
      <GuideExample title="Hesaplama">
        Kontrol 2.00% · Varyasyon 2.50% → <strong style={{ color: BRAND.green }}>+25%</strong>
      </GuideExample>
      <GuideCallout variant="warn">
        Negatif uplift (ör. −10%) varyasyonun kontrolden daha kötü performans gösterdiği anlamına gelir.
      </GuideCallout>
    </GuideSection>

    <GuideSection
      id="bayesian"
      number="04"
      title="Significance (İstatistiksel Anlamlılık)"
      lead="Sonuçların gerçek bir fark mı yoksa şans eseri mi olduğunu gösterir."
    >
      <GuideSubtitle>Nasıl hesaplanır?</GuideSubtitle>
      <p style={{ fontFamily: BRAND.fontFamily, fontSize: 14, color: BRAND.textGray, lineHeight: 1.65, marginBottom: 16 }}>
        Sistem <strong>Monte Carlo simülasyonu</strong> kullanır: 50.000 iterasyonda her grubun olası performansı
        örneklenir; varyasyonun kaç kez kazandığı sayılır ve yüzdeye çevrilir.
      </p>
      <ol className="guide-steps">
        <li>50.000 simülasyon çalıştırılır</li>
        <li>Her turda hangi grubun daha iyi olduğu kaydedilir</li>
        <li>Kazanma olasılığı = kazanma sayısı / 50.000</li>
      </ol>

      <GuideExample title="Örnek sonuç">
        Varyasyon 45.000 simülasyonda kazanır → Significance ≈ <strong>%90</strong>
      </GuideExample>

      <GuideSubtitle>Güven seviyesi (%95 varsayılan)</GuideSubtitle>
      <GuideCallout variant="success">
        <strong>≥ %95:</strong> Sonuç istatistiksel olarak anlamlı — varyasyonu uygulayabilirsiniz.
      </GuideCallout>
      <GuideCallout variant="warn">
        <strong>&lt; %95:</strong> Henüz yeterince güvenilir değil — daha fazla veri toplayın.
      </GuideCallout>

      <GuideTable
        columns={['Significance', 'Anlam', 'Öneri']}
        rows={[
          ['%98', 'Çok güvenilir', 'Varyasyonu uygulayın'],
          ['%85', 'Orta güven', 'Test süresini uzatın'],
          ['%60', 'Düşük güven', 'Daha fazla veri toplayın'],
        ]}
      />
    </GuideSection>

    <GuideSection
      id="revenue"
      number="05"
      title="Monthly / Yearly Extra Transactions"
      lead="Kazanan varyasyon tüm trafiğe uygulanırsa elde edilebilecek ek dönüşüm ve gelir tahmini."
    >
      <GuideFormula label="Adım 1" formula="Mutlak İyileşme = Varyasyon CR − Kontrol CR" />
      <GuideFormula label="Adım 2" formula="Günlük Ekstra Dönüşüm = Günlük Toplam Trafik × Mutlak İyileşme" />
      <GuideFormula label="Adım 3" formula="Aylık Ekstra = Günlük Ekstra × 30 · Yıllık Ekstra = Günlük Ekstra × 365" />

      <GuideExample title="Örnek">
        Kontrol 2.00% · Varyasyon 2.50% · 14 gün · 280.000 toplam ziyaretçi
        <br />
        <br />
        Mutlak iyileşme = 0.50% · Günlük trafik = 20.000 · Günlük ek = <strong>100 dönüşüm</strong>
        <br />
        Aylık ≈ 3.000 · Yıllık ≈ 36.500 ek dönüşüm
      </GuideExample>
      <p style={{ fontFamily: BRAND.fontFamily, fontSize: 13, color: BRAND.textMuted, fontStyle: 'italic' }}>
        Bu değerler projeksiyondur; sezonluk etkiler ve trafik değişimleri sonucu etkileyebilir.
      </p>
    </GuideSection>

    <GuideSection id="summary" number="06" title="Özet Tablo">
      <GuideTable
        columns={['Metrik', 'Ne gösterir', 'Formül']}
        rows={[
          ['Conv. Rate', '100 ziyaretçiden kaçının dönüştüğü', '(Dönüşüm / Ziyaretçi) × 100'],
          ['Uplift', 'Kontrole göre iyileşme', '((Var CR − Kontrol CR) / Kontrol CR) × 100'],
          ['Significance', 'Sonucun güvenilirliği', 'Monte Carlo (50.000 iterasyon)'],
          ['Monthly/Yearly', 'Ek kazanç potansiyeli', 'Günlük trafik × Mutlak iyileşme × gün'],
        ]}
      />
    </GuideSection>

    <GuideSection id="faq" number="07" title="Sık Sorulan Sorular">
      <GuideFaq
        items={[
          {
            q: 'Significance neden önemli?',
            a: 'Düşük significance, sonucun şans eseri olabileceğini gösterir. Karar vermeden önce yeterli güven seviyesine ulaşmak gerekir.',
          },
          {
            q: '%95 güven seviyesi ne demek?',
            a: 'Sonucun %95 olasılıkla doğru olduğu varsayılır — endüstride yaygın kabul gören eşiktir.',
          },
          {
            q: 'Negatif uplift ne anlama gelir?',
            a: 'Varyasyon kontrolden kötü performans gösteriyor; uygulanmaması önerilir.',
          },
          {
            q: 'Monthly/Yearly değerleri kesin mi?',
            a: 'Hayır, tahmindir. Trafik, sezon ve pazar koşulları gerçek sonucu değiştirebilir.',
          },
          {
            q: 'Birden fazla varyasyon varsa?',
            a: 'Her varyasyon kontrol ile ayrı karşılaştırılır. En yüksek significance ve uplift genelde kazanan adayıdır.',
          },
        ]}
      />
      <GuideCallout variant="info">
        Sorularınız için{' '}
        <Link component={RouterLink} to="/" style={{ color: BRAND.purple, fontWeight: 600 }}>
          hype.com.tr
        </Link>{' '}
        üzerinden bizimle iletişime geçebilirsiniz.
      </GuideCallout>
    </GuideSection>
  </GuidePageShell>
);

export default ReportCalculationGuide;
